import { useRef, useState, type ReactElement } from 'react';
import { Database, Loader2 } from 'lucide-react';
import { Dialog } from '../components/Dialog.js';
import { apiPostForm, ApiError } from '../api/client.js';
import { useToast } from '../hooks/useToast.js';

const DB_PATH = '<ROOT>/data/data.sqlite';

export function DatabaseCorruptScreen(): ReactElement {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  function openPicker(): void {
    fileInputRef.current?.click();
  }

  function onFileChange(ev: React.ChangeEvent<HTMLInputElement>): void {
    const file = ev.target.files?.[0] ?? null;
    if (!file) return;
    setPendingFile(file);
    setConfirmOpen(true);
  }

  function cancelConfirm(): void {
    setConfirmOpen(false);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function confirmRestore(): Promise<void> {
    const file = pendingFile;
    if (!file) {
      cancelConfirm();
      return;
    }
    setConfirmOpen(false);
    setRestoring(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await apiPostForm<{ ok: true }>('/api/admin/restore', form);
      toast.push('success', 'Database restored — reloading…');
      window.setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Restore failed';
      toast.push('error', message);
      setRestoring(false);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const buttonBaseStyle = {
    background: 'var(--accent)',
    color: 'var(--accent-contrast, white)',
    borderColor: 'var(--accent)',
  } as const;

  return (
    <main
      className="flex min-h-dvh items-center justify-center p-6"
      style={{ background: 'var(--bg-primary)', color: 'var(--text-main)' }}
      data-testid="db-corrupt-screen"
    >
      <div
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border p-6 shadow-sm"
        style={{
          background: 'var(--bg-surface)',
          borderColor: 'var(--border)',
        }}
      >
        <header className="flex flex-col items-center gap-2 text-center">
          <Database
            size={40}
            aria-hidden="true"
            style={{ color: 'var(--danger, #c0392b)' }}
          />
          <h1 className="text-2xl font-semibold">Database is corrupt</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            The on-disk SQLite file could not be opened. Please restore from a
            known-good backup to recover normal operation.
          </p>
        </header>

        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Path:
        </p>
        <code
          className="block break-all rounded-md border px-3 py-2 text-xs"
          style={{
            background: 'var(--bg-primary)',
            borderColor: 'var(--border)',
            color: 'var(--text-main)',
          }}
          data-testid="db-corrupt-db-path"
        >
          {DB_PATH}
        </code>

        <input
          ref={fileInputRef}
          type="file"
          accept=".sqlite,.db"
          onChange={onFileChange}
          style={{ display: 'none' }}
          data-testid="db-corrupt-file-input"
        />

        <button
          type="button"
          onClick={openPicker}
          disabled={restoring}
          data-testid="db-corrupt-pick-button"
          className="flex w-full items-center justify-center rounded-md border px-3 py-2 text-sm font-medium"
          style={{
            ...buttonBaseStyle,
            minHeight: 48,
            opacity: restoring ? 0.7 : 1,
          }}
        >
          {restoring ? (
            <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />
          ) : null}
          {restoring ? 'Restoring…' : 'Restore Backup File'}
        </button>

        <Dialog
          open={confirmOpen}
          onClose={cancelConfirm}
          title="Replace current database?"
        >
          <p className="mb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
            The current (corrupt) database will be permanently replaced by{' '}
            <strong style={{ color: 'var(--text-main)' }}>
              {pendingFile?.name ?? 'selected file'}
            </strong>
            . This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={cancelConfirm}
              data-testid="db-corrupt-cancel-button"
              className="rounded-md border px-3 py-2 text-sm font-medium"
              style={{
                background: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                color: 'var(--text-main)',
                minHeight: 44,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void confirmRestore()}
              data-testid="db-corrupt-confirm-button"
              className="rounded-md border px-3 py-2 text-sm font-medium"
              style={{
                background: 'var(--danger, #c0392b)',
                borderColor: 'var(--danger, #c0392b)',
                color: 'white',
                minHeight: 44,
              }}
            >
              Replace database
            </button>
          </div>
        </Dialog>
      </div>
    </main>
  );
}
